import React, { useRef, useEffect, useState } from "react";
import avatarAssets from "../utils/avatarAssets";

// A simpler version of VRMStatic for preview purposes
const PreviewVRM = ({ avatar }) => {
  const containerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const vrmRef = useRef({
    renderer: null,
    scene: null,
    camera: null,
    mixer: null,
    clock: null,
    currentVrm: null,
    animationId: null
  });

  useEffect(() => {
    console.log('[PreviewVRM] Effect triggered with avatar:', avatar);
    const vrmData = vrmRef.current;
    let isComponentMounted = true;

    // Get VRM path from avatar object - moved inside useEffect to avoid dependency issues
    const getVrmPath = () => {
      console.log('[PreviewVRM] Input avatar:', JSON.stringify(avatar, null, 2));
      
      if (avatar?.vrmPath) {
        console.log('[PreviewVRM] Avatar has vrmPath property, using directly:', avatar.vrmPath);
        // If vrmPath is already a full URL, use it directly
        if (avatar.vrmPath.startsWith('http://') || avatar.vrmPath.startsWith('https://')) {
          return avatar.vrmPath;
        }
        // Otherwise, resolve it through the assets manager
        return avatarAssets.getFullAssetUrl(avatar.vrmPath);
      }
      if (avatar?.id) {
        console.log('[PreviewVRM] Using avatar.id:', avatar.id);
        const avatarObj = avatarAssets.getAvatarById(avatar.id);
        console.log('[PreviewVRM] Found avatar object:', avatarObj);
        const path = avatarAssets.getVrmPath(avatar.id);
        console.log('[PreviewVRM] Resolved VRM path for ID:', path);
        return path;
      }
      if (typeof avatar === 'string') {
        console.log('[PreviewVRM] Avatar is string:', avatar);
        // Check if it's already a full URL (this shouldn't happen in normal flow)
        if (avatar.startsWith('http://') || avatar.startsWith('https://')) {
          console.log('[PreviewVRM] Avatar string is already a full URL, using directly');
          return avatar;
        }
        const path = avatarAssets.getVrmPath(avatar);
        console.log('[PreviewVRM] Resolved VRM path for string:', path);
        return path;
      }
      const defaultPath = avatarAssets.getDefaultAvatar().vrmPath;
      console.log('[PreviewVRM] Using default avatar:', defaultPath);
      return defaultPath;
    };

    const clearCurrentVRM = () => {
      console.log('[PreviewVRM] Clearing current VRM model');
      
      if (vrmData.mixer) {
        vrmData.mixer.stopAllAction();
        vrmData.mixer = null;
      }
      
      if (vrmData.scene && vrmData.currentVrm) {
        vrmData.scene.remove(vrmData.currentVrm.scene);
        if (vrmData.currentVrm.dispose) {
          vrmData.currentVrm.dispose();
        }
        vrmData.currentVrm = null;
        console.log('[PreviewVRM] VRM model cleared from scene');
      }
    };

    const loadVRMModel = () => {
      if (!vrmData.scene || !isComponentMounted) return;
      
      // Clear any existing VRM
      console.log('[PreviewVRM] Clearing existing VRM before loading new one');
      clearCurrentVRM();
      
      // Show loading spinner
      setIsLoading(true);
      
      const loader = new window.THREE.GLTFLoader();
      loader.crossOrigin = 'anonymous';
      
      const vrmPath = getVrmPath();
      // Add cache-busting parameter to prevent browser/THREE.js caching
      const cacheBuster = Date.now();
      const vrmPathWithCacheBuster = `${vrmPath}?_cb=${cacheBuster}`;
      console.log(`[PreviewVRM] Loading VRM model with cache buster: ${vrmPathWithCacheBuster}`);
      
      loader.load(
        vrmPathWithCacheBuster,
        (gltf) => {
          if (!isComponentMounted) return;
          
          window.THREE.VRMUtils.removeUnnecessaryJoints(gltf.scene);
          
          window.THREE.VRM.from(gltf).then((vrm) => {
            if (!isComponentMounted) return;
            
            console.log('[PreviewVRM] VRM parsed successfully, adding to scene');
            vrmData.scene.add(vrm.scene);
            vrmData.currentVrm = vrm;
            vrm.scene.rotation.y = Math.PI; // Face the camera
            
            // Center the model
            const bbox = new window.THREE.Box3().setFromObject(vrm.scene);
            const center = bbox.getCenter(new window.THREE.Vector3());
            vrm.scene.position.x = -center.x;
            vrm.scene.position.z = -center.z;
            
            console.log('[PreviewVRM] VRM model positioned and added to scene');
            
            // Create idle animation - simple head motion
            const head = vrm.humanoid.getBoneNode(window.THREE.VRMSchema.HumanoidBoneName.Head);
            
            if (head) {
              // Create animation mixer
              const mixer = new window.THREE.AnimationMixer(head);
              vrmData.mixer = mixer;
              
              // Create a simple idle animation - gentle head movement
              const tracks = [
                new window.THREE.NumberKeyframeTrack(
                  '.rotation[x]', 
                  [0, 2, 4, 6, 8], 
                  [0, 0.05, 0, -0.05, 0]
                ),
                new window.THREE.NumberKeyframeTrack(
                  '.rotation[y]', 
                  [0, 3, 6, 9, 12], 
                  [0, 0.1, 0, -0.1, 0]
                )
              ];
              
              const clip = new window.THREE.AnimationClip('idle', 12, tracks);
              const action = mixer.clipAction(clip);
              action.setLoop(window.THREE.LoopRepeat);
              action.play();
              
              console.log('[PreviewVRM] Idle animation started');
            }
            
            console.log('[PreviewVRM] VRM model loaded and initialized successfully');
            
            // Hide loading spinner
            setIsLoading(false);
          });
        },
        (progress) => console.log(`[PreviewVRM] Loading progress: ${(progress.loaded / progress.total * 100).toFixed(1)}%`),
        (error) => {
          console.error('[PreviewVRM] Error loading VRM model:', error);
          // Hide loading spinner on error
          setIsLoading(false);
        }
      );
    };

    const initialize = () => {
      if (!window.THREE) {
        console.error("THREE.js not loaded");
        return;
      }
      
      // Create renderer
      const renderer = new window.THREE.WebGLRenderer({ 
        alpha: true, 
        antialias: true,
        canvas: containerRef.current 
      });
      renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
      renderer.setPixelRatio(window.devicePixelRatio || 1);
      renderer.setClearColor(0x000000, 0); // Transparent background
      vrmData.renderer = renderer;

      // Create camera
      const camera = new window.THREE.PerspectiveCamera(
        35, 
        containerRef.current.clientWidth / containerRef.current.clientHeight, 
        0.1, 
        1000
      );
      camera.position.set(0, 1.3, 1.4); // Position further back for better framing in preview
      vrmData.camera = camera;

      // Create scene
      const scene = new window.THREE.Scene();
      vrmData.scene = scene;

      // Add lights
      const light = new window.THREE.DirectionalLight(0xffffff, 1);
      light.position.set(1.0, 1.0, 1.0).normalize();
      scene.add(light);
      
      const ambientLight = new window.THREE.AmbientLight(0xffffff, 0.5);
      scene.add(ambientLight);

      // Setup clock for animations
      vrmData.clock = new window.THREE.Clock();

      // Load initial VRM model
      loadVRMModel();

      // Animation loop
      const animate = () => {
        if (!isComponentMounted) return;
        
        vrmData.animationId = requestAnimationFrame(animate);
        
        const delta = vrmData.clock.getDelta();
        
        if (vrmData.mixer) {
          vrmData.mixer.update(delta);
        }
        
        if (vrmData.currentVrm) {
          vrmData.currentVrm.update(delta);
        }
        
        renderer.render(scene, camera);
      };
      
      animate();
    };

    // If scene already exists, just reload the VRM model
    if (vrmData.scene) {
      loadVRMModel();
    } else {
      // Check if THREE.js is loaded, if so initialize
      const checkScriptsLoaded = () => {
        if (window.THREE) {
          initialize();
        } else {
          // If not loaded yet, wait a bit and check again
          setTimeout(checkScriptsLoaded, 100);
        }
      };
      
      checkScriptsLoaded();
    }

    // Handle window resize
    const handleResize = () => {
      if (!containerRef.current || !vrmData.camera || !vrmData.renderer) return;
      
      vrmData.camera.aspect = containerRef.current.clientWidth / containerRef.current.clientHeight;
      vrmData.camera.updateProjectionMatrix();
      vrmData.renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    };
    
    window.addEventListener('resize', handleResize);

    return () => {
      isComponentMounted = false;
      window.removeEventListener('resize', handleResize);
      
      if (vrmData.animationId) {
        cancelAnimationFrame(vrmData.animationId);
      }
      
      if (vrmData.scene && vrmData.currentVrm) {
        vrmData.scene.remove(vrmData.currentVrm.scene);
        if (vrmData.currentVrm.dispose) {
          vrmData.currentVrm.dispose();
        }
      }
      
      if (vrmData.renderer) {
        vrmData.renderer.dispose();
      }
    };
  }, [avatar]); // Add avatar as dependency to reload when it changes

  return (
    <div style={{
      width: "100%",
      height: "100%",
      position: "relative",
      backgroundColor: "rgba(60, 16, 51, 0.7)", // Darker background for better contrast
      borderRadius: "8px"
    }}>
      <canvas
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
          backgroundColor: "transparent",
          display: "block", // Ensure proper display
          zIndex: 10 // Ensure canvas is on top
        }}
      />
      
      {/* Loading Spinner */}
      {isLoading && (
        <div className="vrm-loading-overlay">
          <div className="vrm-spinner vrm-spinner-medium"></div>
          <div className="vrm-loading-text">
            Loading...
          </div>
        </div>
      )}
      
      <div style={{ 
        position: "absolute",
        bottom: "10px",
        right: "10px",
        color: "#fff",
        fontSize: "12px",
        opacity: 0.7,
        zIndex: 20
      }}>
        VRM Preview
      </div>
    </div>
  );
};

export default PreviewVRM;
